<template>
  <div class="card shadow mb-4">
    <div class="card-header py-3 d-flex flex-row align-items-center justify-content-between">
      <h6 v-if="current_page=='Substations'" class="m-0 font-weight-bold">
        {{ $t('System_data') }} 
        <span v-if="device_type == 'battery'">({{ $t('Battery') }})</span>
        <span v-else>({{ $t('Load') }})</span>
      </h6>
      <h6 v-else class="m-0 font-weight-bold">Samples</h6>
      <div v-show="device_type != 'battery'" class="form-inline"> 
        <div class="form-group">
          <label class="mr-3" for="select">Ver:</label>
          <select @change="onChange($event)" name="select" id="select" class="form-control custom-select">
            <option value="all" selected>{{ $t('All') }}</option>
            <option value="I">{{ $t('Current_electricity') }} (I)</option>
            <option value="PF">{{ $t('Power_factor') }} (PF)</option>
            <option value="F">{{ $t('Frequency') }} (F)</option>
            <option value="S">{{ $t('Apparent_power') }} (S)</option>
            <option value="P">{{ $t('Active_power') }} (P)</option>
            <option value="Q">{{ $t('Reactive_power') }} (Q)</option>
            <option value="V">{{ $t('Voltage') }} (V)</option>
          </select>
        </div>
      </div>
    </div>
    <div class="card-body text-center">
      <LoadingSpinner v-if="loading || local_loading"></LoadingSpinner>
      <div v-show="!loading && !local_loading" id="canvas-sampleschart"></div>
      <div v-show="!loading && !local_loading" :id="'canvas-sampleschart-'+device_type"></div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from './LoadingSpinner.vue'

export default {
  name: 'SamplesChart',
  props: ["device_type"],
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      chart: null,
      selected_option: "all",
      previous_selected_option: "all",
      local_loading: false
    }
  }, 
  mounted: function(){
    const chart_options = {
        chart: {
          zoomType: '',
          backgroundColor: '#fff'
        },
        title: {
          text: null
        },
        credits: { enabled: false },
        xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
              day: '%a, %e %b'
          }
        },
        yAxis: {
          title: {
            text: '',
            rotation: 0,
          }
        },
        legend: {
          enabled: true
        },
        tooltip: {
          shared: true,
          xDateFormat: '%A, %d %b, %H:%M',
          pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f}</b>'
        },
        plotOptions: {
          area: {
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1
              },
              stops: [
                [0, Highcharts.getOptions().colors[0]],
                [1, Highcharts.Color(Highcharts.getOptions().colors[0]).setOpacity(0).get('rgba')]
              ]
            },
            marker: {
              radius: 2
            },
            lineWidth: 1,
            states: {
              hover: {
                lineWidth: 1
              }
            },
            threshold: null
          },
          series: {
            connectNulls: false,
            marker: {
              enabled: false
            }
          },
        },
      series: []
    }

    if (this.device_type) this.chart = Highcharts.chart('canvas-sampleschart-'+this.device_type, chart_options)
    else this.chart = Highcharts.chart('canvas-sampleschart', chart_options)
  },
  methods: {
    onChange: function(event) {
      this.selected_option = event.target.value
    },
    set_values_flatten: function() {
      while (this.chart.series.length > 0) this.chart.series[0].remove(true)

      if (!this.data.length || !this.data[0].samples.length) return 

      this.local_loading = true
      let fields_to_ignore = ["datetime", "measure_cons", "measure_grid", "bmsSocTrimmedAvg"]
     /* for (let p of ["L1", "L2", "L3"]) {
        for (let f of ["S", "V", "I", "PF", "P", "Q"])
          fields_to_ignore.push(p+"."+f)
      } */

      //let fields_to_use = ["P", "Q"] 


      const timezone = this.$store.getters.timezone
      const device_types = this.device_type == 'battery' ? ['battery'] : ['consumption', 'grid']
      for (let d of this.data[0].samples.filter(dt => device_types.includes(dt.type))) {
        let flag = 0
        for (let s of d.data) {
          for (let field in s) {
            //if (!fields_to_use.includes(field)) continue
            if (fields_to_ignore.includes(field)) continue
            if (this.chart.series.find(serie => (serie.options.custom.device_type == d.type) && (serie.options.custom.field == field))) {
              const series_index = this.chart.series.findIndex(serie => (serie.options.custom.device_type == d.type) && (serie.options.custom.field == field))
              this.chart.series[series_index].addPoint([moment.tz(s.datetime, timezone).valueOf(), parseFloat(s[field])])
            }
            else { 
              const type = d.type == 'battery' ? this.$i18n.t('Battery') : this.$i18n.t('Load') 
              this.chart.addSeries({
                name: `${field}`,
                custom: {
                  field: field,
                  device_type: d.type
                },
                type: 'spline',
                visible: flag ? false : true,
              }, false)
              flag = d.type == 'battery' ? 0 : 1
              this.chart.series[this.chart.series.length-1].addPoint([moment.tz(s.datetime, timezone).valueOf(), parseFloat(s[field])])
            }
          }
        }
      }
      this.local_loading = false
      this.chart.redraw() 
    },
    set_values: async function(){
      const timezone = this.$store.getters.timezone
      while (this.chart.series.length > 0) this.chart.series[0].remove(true)
      
      let data = this.data
      
      if (data.length && this.device_type) {
        const device_types = this.device_type == 'battery' ? ['battery'] : ['consumption', 'grid']
        data = data.find(dt => device_types.includes(dt.type))
        if (data != undefined) data = data.data
      }

      let samples = []
      let fields = []

      const fields_to_ignore = ['_id._bsontype', '_id', 'datetime', 'timestamp', '_id.id', 'producer', 'device', 'data']

      data.forEach(r => {
      //this.data.forEach(r => {
        let output = this.flatten(r)
        samples.push(output)
        fields = []
        let fields_all = Object.keys(output)
        for (let f of fields_all) {
          if (!fields_to_ignore.includes(f) && !f.match(/data.\d+$/g)) {
            if (f.match(/measure_.\D+$/g) && this.current_page == "Substations") continue
            fields.push(f)
          }
        }    
      })
      
      let flag = 0
      for (let f of fields) {                    
        this.chart.addSeries({
          name: f,
          type: 'spline',
          visible: flag ? false : true,
          data: samples.map(s => [moment.tz(s.timestamp || s.datetime, timezone).valueOf(), parseFloat(s[f])]),
        }, false)
        flag = 1
      }      
      //this.chart.redraw() 
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          this.chart.redraw()
          this.selected_option = 0
          resolve()
        }, 0)
      })
      this.selected_option = $("#select").val()
    },
    flatten: function(target, opts){
      opts = opts || {}
       
      var delimiter = opts.delimiter || '.'
      var maxDepth = opts.maxDepth
      var output = {}
       
      function step (object, prev, currentDepth) {
        currentDepth = currentDepth || 1
        Object.keys(object).forEach(function (key) {
          var value = object[key]
          var isarray = opts.safe && Array.isArray(value)
          var type = Object.prototype.toString.call(value)
          var isobject = (
            type === '[object Object]' ||
            type === '[object Array]'
          )
      
          var newKey = prev
            ? prev + delimiter + key
            : key
      
          if (!isarray && isobject && Object.keys(value).length &&
            (!opts.maxDepth || currentDepth < maxDepth)) {
            return step(value, newKey, currentDepth + 1)
          }
      
          output[newKey] = value
        })
      }
      
      step(target)
      return output
    }
  },
  computed:{
    data: function(){
      if (this.current_page == "Substations") 
        return this.$store.getters.data.length ? this.$store.getters.data[0].samples : []
      return this.$store.getters.data
    },
    loading: function(){
      return this.$store.getters.data_loading
    },
    current_page: function(){
      return this.$route.name
    }
  },
  watch:{
    data: function(){
      /* if (this.current_page == "Substations") {
        this.set_values_flatten()
      }
      else this.set_values() */
      this.set_values()
      //this.selected_option = 'all'
    },
    selected_option: function() {
      if (!this.selected_option) return
      this.previous_selected_option = this.selected_option

      this.chart.series.forEach((s, i) => {
        if (this.selected_option == "all") {
          this.chart.series[i].update({ showInLegend: true })
          this.chart.series[i].update({ visible: false })
          this.chart.yAxis[0].update({ title: { text: '' } })
          this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f}</b>'})
          if (i == 0) this.chart.series[i].update({ visible: true })
          return
        }

        if (
          s.name != `L1.${this.selected_option}` && 
          s.name != `L2.${this.selected_option}` && 
          s.name != `L3.${this.selected_option}` &&
          s.name != this.selected_option) {
          this.chart.series[i].update({ showInLegend: false })
          this.chart.series[i].update({ visible: false })
        }
        else {
          switch (this.selected_option) {
            case "V": {
              this.chart.yAxis[0].update({ title: { text: 'V' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} V</b>'})
              break
            }
            case "I": {
              this.chart.yAxis[0].update({ title: { text: 'A' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} A</b>'})
              break
            }
            case "F": {
              this.chart.yAxis[0].update({ title: { text: 'Hz' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} Hz</b>'})
              break
            }
            case "PF": {
              this.chart.yAxis[0].update({ title: { text: '' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f}</b>'})
              break
            }
            case "S": {
              this.chart.yAxis[0].update({ title: { text: 'VA' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} VA</b>'})
              break
            }
            case "Q": {
              this.chart.yAxis[0].update({ title: { text: 'VAr' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} VAr</b>'})
              break
            }
            default: {
              this.chart.yAxis[0].update({ title: { text: 'W' } })
              this.chart.tooltip.update({ pointFormat: '<br/><span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} W</b>'})
              break
            }
          }
          this.chart.series[i].update({ showInLegend: true })
          this.chart.series[i].update({ visible: true })
        } 
      })
      this.chart.redraw()
      return 
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
